import { Star } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Modal } from '@/components/ui/modal'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useCreateReview } from '../../api'
import { Field, ModalFooter } from './Field'
import { apiErrorMessage } from '@/i18n'

const PUANLAR = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5]

export function DegerlendirmeModal({ personelId, onClose }: { personelId: string; onClose: () => void }) {
  const { t } = useTranslation()
  const [skor, setSkor] = useState(4)
  const create = useCreateReview()
  const [metin, setMetin] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const err = submitted && metin.trim().length < 10 ? t('hr.personel.actions.degerlendirme.notMin') : undefined

  const submit = () => {
    setSubmitted(true)
    if (metin.trim().length < 10) return
    create.mutate(
      { employeeId: personelId, score: skor, body: metin.trim() },
      {
        onSuccess: () => {
          toast.success(t('hr.personel.actions.degerlendirme.created'))
          onClose()
        },
        onError: (error) => toast.error(apiErrorMessage(error)),
      },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      icon={<Star />}
      title={t('hr.personel.actions.degerlendirme.title')}
      description={t('hr.personel.actions.degerlendirme.hint')}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      footer={<ModalFooter onCancel={onClose} />}
    >
      <div className="space-y-4">
        <Field id="deg-skor" label={t('hr.personel.actions.degerlendirme.skor')}>
          <div className="flex flex-wrap items-center gap-3">
            <div id="deg-skor" role="radiogroup" className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={Math.round(skor) === n}
                  aria-label={`${n} / 5`}
                  onClick={() => setSkor(n)}
                  className="rounded-md p-1 text-warning transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                >
                  <Star className={cn('size-6', n <= Math.round(skor) ? 'fill-current' : 'opacity-35')} />
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1">
              {PUANLAR.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setSkor(p)}
                  className={cn(
                    'h-7 min-w-9 rounded-lg border px-2 text-xs font-medium tabular-nums transition-colors',
                    skor === p ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card hover:bg-muted',
                  )}
                >
                  {p.toFixed(1).replace('.', ',')}
                </button>
              ))}
            </div>
          </div>
        </Field>
        <Field id="deg-not" label={t('hr.personel.actions.degerlendirme.not')} error={err}>
          <Textarea id="deg-not" rows={4} maxLength={600} className="rounded-xl" value={metin} onChange={(e) => setMetin(e.target.value)} aria-invalid={!!err} />
        </Field>
      </div>
    </Modal>
  )
}
